import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Calendar, Check, Film, Smartphone, TrendingUp, Camera, Search, Paperclip, X, FileText, Send, Mail, Phone, MapPin } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { SEO, breadcrumbSchema, faqSchema } from "@/components/site/SEO";
import { FAQ } from "@/components/site/FAQ";
import { PortfolioCategoryStrip } from "@/components/site/PortfolioCategoryStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AGENCY_CLIENTS, AGENCY_CAPABILITIES, AGENCY_LEAD_MAGNET, AGENCY_TIERS, AGENCY_FAQS, AGENCY_HERO_IMAGE, AGENCY_SHOWCASE } from "@/lib/agenciesData";
import { SITE, CALENDLY_URL } from "@/lib/siteData";
import { trackLeadSubmission, getGaClientId } from "@/lib/analytics";
import { OptimaGrowthPanel } from "@/components/site/OptimaGrowthPanel";
import { ROUTE_SEO } from "@/lib/routeSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CAPABILITY_ICONS = { Film, Smartphone, TrendingUp, Camera, Search, Sparkles };
const MAX_FILES = 3;
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const formatSize = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;

const LeadMagnetForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", brief: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const next = [...files];
    let total = files.reduce((s, f) => s + f.size, 0);
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { toast.error(`Up to ${MAX_FILES} files.`); break; }
      if (total + f.size > MAX_TOTAL_BYTES) { toast.error("Combined size exceeds 10 MB."); break; }
      next.push(f); total += f.size;
    }
    setFiles(next); e.target.value = "";
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!form.brief.trim() && files.length === 0) { toast.error("Paste a brief outline or attach a file."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name); fd.append("email", form.email);
      if (form.company) fd.append("company", form.company);
      fd.append("brief", form.brief || "(brief attached)");
      fd.append("source", "landing-marketing-agencies");
      fd.append("industry", "Marketing & Advertising Agencies");
      fd.append("package_interest", "Free AI Concept Reel + Treatment");
      files.forEach((f) => fd.append("files", f));
      const gaCid = getGaClientId();
      if (gaCid) fd.append("ga_client_id", gaCid);
      await axios.post(`${BACKEND_URL}/api/enquiry`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackLeadSubmission({ source: "agencies-landing", formName: "Agencies landing page", value: 1000 });
      toast.success("Brief received - your concept reel will land in 72 hours.");
      setForm({ name: "", email: "", company: "", brief: "" }); setFiles([]);
      navigate("/thank-you?source=agencies-landing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <form data-testid="agency-leadmagnet-form" onSubmit={submit} className="space-y-4" encType="multipart/form-data" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your name</Label><Input data-testid="agency-name" value={form.name} onChange={update("name")} placeholder="First & last" className="bg-background border-border h-11" required /></div>
        <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label><Input data-testid="agency-email" type="email" value={form.email} onChange={update("email")} placeholder="you@agency.com" className="bg-background border-border h-11" required /></div>
      </div>
      <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Agency / company</Label><Input data-testid="agency-company" value={form.company} onChange={update("company")} placeholder="Your agency" className="bg-background border-border h-11" /></div>
      <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Client brief - paste a short outline (optional if attaching)</Label><Textarea data-testid="agency-brief" value={form.brief} onChange={update("brief")} placeholder="Who's the client, what's the campaign, what's the deadline. 3-4 lines is plenty." className="bg-background border-border min-h-[110px]" /></div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Attach brief (PDF, deck, doc) <span className="text-muted-foreground/60 normal-case tracking-normal">- up to 3 files / 10 MB</span></Label>
        <div className="rounded-xl border border-dashed border-border bg-background p-4">
          <input id="agency-file-input" type="file" multiple accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg" className="hidden" onChange={onFiles} data-testid="agency-file-input" />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" data-testid="agency-attach-btn" onClick={() => document.getElementById("agency-file-input").click()} variant="ghost" className="h-10 rounded-full border border-border bg-card hover:bg-[hsl(225,18%,9%)] px-4"><Paperclip className="mr-2 h-4 w-4" />Attach brief</Button>
            <span className="text-xs text-muted-foreground">Confidential. Treated under NDA.</span>
          </div>
          {files.length > 0 && (
            <ul className="mt-3 space-y-2">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between gap-3 rounded-lg bg-card/60 border border-border px-3 py-2">
                  <div className="flex items-center gap-3 min-w-0"><FileText className="h-4 w-4 text-[hsl(var(--primary))] shrink-0" /><div className="min-w-0"><div className="text-sm text-foreground truncate">{f.name}</div><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{formatSize(f.size)}</div></div></div>
                  <button type="button" onClick={() => removeFile(i)} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Button data-testid="agency-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 text-sm font-medium disabled:opacity-60">
        {submitting ? "Sending…" : (<>Get my concept reel <Send className="ml-2 h-4 w-4" /></>)}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center">72-hour turnaround. Free. No obligation. We stay invisible to your client.</p>
    </form>
  );
};

export default function AgenciesLanding() {
  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/for/marketing-agencies"].title}
        description={ROUTE_SEO["/for/marketing-agencies"].meta}
        path="/for/marketing-agencies"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "For Marketing Agencies" }]), faqSchema(AGENCY_FAQS.map(f => ({ question: f.q, answer: f.a })))]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={AGENCY_HERO_IMAGE} alt="Marketing agency video campaign filmed by Pie in the Sky Productions in Cape Town" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
        </div>
        <div className="container-site pt-24 pb-20 sm:pt-32 sm:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))/0.4] bg-[hsl(var(--primary))/0.08] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]"><Sparkles className="h-3.5 w-3.5" />For Agencies</span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">In partnership with OptimaGrowth</span>
              </div>
              <h1 className="sr-only">Video Production for Marketing Agencies</h1>
              <h2 data-testid="agency-hero-title" className="font-display uppercase text-foreground text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[0.92] tracking-[-0.005em] text-balance">
                Your agency&apos;s <span className="text-[hsl(var(--primary))]">unfair creative edge.</span>
              </h2>
              <p className="mt-6 text-base sm:text-lg text-foreground/85 max-w-2xl leading-relaxed">
                Cinematic video + AI-powered creative scale + SEO via OptimaGrowth - one production partner, agency-pace turnaround, and we stay invisible to your client. So you look like the hero.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild data-testid="agency-cta-primary" className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><a href="#free-concept-reel">Get a free AI concept reel <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
                <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a 15-min call</a></Button>
              </div>
              <p className="mt-6 text-xs uppercase tracking-[0.22em] text-white/65">Trusted by · Manchester City · McDonald&apos;s · Woolworths · Takealot · BMW · Stellenbosch University · Roche</p>
            </div>
            <div className="lg:col-span-5">
              <OptimaGrowthPanel
                testId="agency-optimagrowth-panel"
                title="Marketing SEO partner"
                body="Your video gets found. OptimaGrowth handles keyword strategy, on-page SEO and content velocity for your client's owned channels - tied directly into our production pipeline. Billed separately by OptimaGrowth."
                bullets={[
                  "Single point of contact - we coordinate both teams",
                  "Quote production + SEO together or separately",
                  "White-label - your agency branding stays in front",
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="section-pad">
        <div className="container-site">
          <p className="kicker mb-3">What we deliver</p>
          <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,4rem)] leading-[0.95] text-balance max-w-3xl">An always-on creative production line for your agency.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AGENCY_CAPABILITIES.map((cap, i) => {
              const Icon = CAPABILITY_ICONS[cap.icon] || Film;
              return (
                <motion.div key={cap.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className="rounded-2xl border border-border bg-card p-7 hover:bg-[hsl(225,18%,9%)] transition-colors">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(225,16%,14%)] border border-border text-[hsl(var(--primary))]"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display text-xl uppercase tracking-[0.02em] text-foreground">{cap.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{cap.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEAD MAGNET - the hero offer */}
      <section id="free-concept-reel" className="section-pad bg-[hsl(225,22%,4%)] border-y border-border scroll-mt-20">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))/0.4] bg-[hsl(var(--primary))/0.08] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-4"><Sparkles className="h-3.5 w-3.5" />{AGENCY_LEAD_MAGNET.badge}</span>
              <h2 className="font-display uppercase text-foreground text-[clamp(2.25rem,5.5vw,4.25rem)] leading-[0.95] text-balance">{AGENCY_LEAD_MAGNET.title}</h2>
              <p className="mt-5 text-sm sm:text-base text-foreground/85 leading-relaxed max-w-xl">{AGENCY_LEAD_MAGNET.subtitle}</p>
              <ul className="mt-6 space-y-2.5 text-sm text-foreground/85">
                {AGENCY_LEAD_MAGNET.bullets.map(b => (<li key={b} className="flex items-start gap-2"><Check className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />{b}</li>))}
              </ul>
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                <div><div className="font-display text-2xl text-foreground">72h</div><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Turnaround</div></div>
                <div><div className="font-display text-2xl text-foreground">0 ZAR</div><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Cost to you</div></div>
                <div><div className="font-display text-2xl text-foreground">NDA</div><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">By default</div></div>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-[hsl(var(--primary))/0.35] bg-card p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display uppercase text-foreground text-xl tracking-[0.02em]">{AGENCY_LEAD_MAGNET.formTitle}</h3>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">Free · 72h</span>
                </div>
                <p className="text-xs text-muted-foreground mb-5">{AGENCY_LEAD_MAGNET.formSubtitle}</p>
                <LeadMagnetForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <PortfolioCategoryStrip
        categories={["commercial", "social-media"]}
        heading="Production output your agency can hide behind."
        subheading="A slice of work delivered for global, agency and direct clients — commercials, brand films, social-first cutdowns and lifestyle."
        ctaLabel="See all commercial work"
      />

      {/* ENGAGEMENT TIERS */}
      <section className="section-pad">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-12">
            <div className="lg:col-span-8">
              <p className="kicker mb-3">How agencies work with us</p>
              <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,4rem)] leading-[0.95] text-balance">Ad hoc, or always-on. <span className="text-[hsl(var(--primary))]">Pick your pace.</span></h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button asChild variant="ghost" className="h-11 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/pricing">See full pricing <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AGENCY_TIERS.map(tier => (
              <div key={tier.name} data-testid={`agency-tier-${tier.badge.toLowerCase()}`} className={`relative rounded-2xl border p-7 sm:p-8 flex flex-col ${tier.featured ? "border-[hsl(var(--primary))/0.5] bg-[hsl(225,18%,8%)]" : "border-border bg-card"}`}>
                {tier.featured && (<span className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary-foreground))]"><Sparkles className="h-3 w-3" />Best value</span>)}
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">{tier.badge}</div>
                <h3 className="mt-1 font-display text-2xl uppercase tracking-[0.02em] text-foreground">{tier.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{tier.headline}</p>
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Investment</div>
                  <div className="font-display text-2xl sm:text-3xl text-foreground leading-tight">{tier.pricingNote}</div>
                  <p className="mt-2 text-xs text-muted-foreground">{tier.pricingSubnote}</p>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-foreground/85 flex-1">
                  {tier.bullets.map(b => (<li key={b} className="flex items-start gap-2"><Check className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" />{b}</li>))}
                </ul>
                {tier.href ? (
                  <Button asChild data-testid={`agency-tier-cta-${tier.badge.toLowerCase()}`} className="mt-7 h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110"><Link to={tier.href}>{tier.cta} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                ) : (
                  <Button asChild data-testid={`agency-tier-cta-${tier.badge.toLowerCase()}`} className="mt-7 h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />{tier.cta}</a></Button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground text-center">Every brief is quoted bespoke - drop us a line and we'll come back within one business day. <Link to="/pricing" className="underline underline-offset-4 hover:text-foreground">See indicative pricing</Link> for guidance.</p>
        </div>
      </section>

      {/* CLIENT TRUST GRID */}
      <section className="section-pad bg-[hsl(225,22%,4%)] border-y border-border">
        <div className="container-site">
          <p className="kicker mb-3">Trusted by world-class brands</p>
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl mb-10">Production output your agency can hide behind.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {AGENCY_CLIENTS.map(c => (
              <div key={c.name} className="rounded-xl border border-border bg-card p-5 hover:bg-[hsl(225,18%,9%)] transition-colors">
                <div className="font-display text-base uppercase tracking-[0.02em] text-foreground">{c.name}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{c.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center"><Button asChild variant="ghost" className="h-11 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/case-studies">Read the case studies <ArrowRight className="ml-2 h-4 w-4" /></Link></Button></div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={AGENCY_FAQS} kicker="Agency questions" title="Everything you might want to ask." />

      {/* FINAL CTA */}
      <section className="section-pad">
        <div className="container-site">
          <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] p-8 sm:p-12">
            <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[hsl(var(--primary))/0.12] blur-3xl" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="kicker mb-3">Ready when you are</p>
                <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.5rem)] leading-[0.95] tracking-[0.01em] text-balance">Send us your client brief. <span className="text-[hsl(var(--primary))]">Get a concept reel back in 72 hours.</span></h2>
                <ul className="mt-5 space-y-2 text-sm text-foreground/85">
                  <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><a className="hover:text-[hsl(var(--primary))]" href={`mailto:${SITE.email}`}>{SITE.email}</a></li>
                  <li className="flex items-start gap-2"><Phone className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><a className="hover:text-[hsl(var(--primary))]" href={`tel:${SITE.phone.replace(/\s+/g,"")}`}>{SITE.phone}</a></li>
                  <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /><span className="text-muted-foreground">{SITE.location}</span></li>
                </ul>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><a href="#free-concept-reel">Submit a brief <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
                <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a 15-min call</a></Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
